import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { MapPin, ArrowLeft } from 'lucide-react';
import { useFossasSepticas } from '../context/FossasSepticasProvider';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { createCustomIcon } from '../utils/mapHelpers';
import { primaryButtonStyle, secondaryButtonStyle, applyDisabledStyle } from '../utils/buttonStyles';
import { 
  getInputStyle, 
  getSelectStyle, 
  labelStyle, 
  errorMessageStyle, 
  formGroupStyle, 
  formContainerStyle, 
  formActionsStyle,
  getFormGridStyle
} from '../utils/formStyles';

// Componente para selecionar localização no mapa
const LocationSelector = ({ position, setPosition }) => {
  useMapEvents({
    click: (e) => {
      setPosition([e.latlng.lat, e.latlng.lng]);
    },
  });

  return position ? (
    <Marker 
      position={position} 
      icon={createCustomIcon('Em Análise')}
    />
  ) : null;
};

/**
 * Componente para cadastro e edição de propriedades rurais
 */
const CadastroPropriedadeRural = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { 
    propriedades, 
    carregarPropriedades, 
    salvarPropriedade, 
    loading,
    localidades,
    agentes,
    carregarLocalidades,
    carregarAgentes
  } = useFossasSepticas();

  // Estado do formulário
  const [formData, setFormData] = useState({
    nome: '',
    nome_proprietario: '',
    cpf_cnpj: '',
    telefone: '',
    status: 'Não Mapeada',
    agente_id: null,
    localidade_id: null,
    latitude: null,
    longitude: null
  });

  // Estado para erros de validação
  const [errors, setErrors] = useState({});
  
  // Estado para posição no mapa
  const [position, setPosition] = useState(null);
  
  // Centro inicial do mapa (Conceição do Castelo - ES)
  const centroConceicaoCastelo = [-20.359292, -41.247282];
  const [mapCenter, setMapCenter] = useState(centroConceicaoCastelo);
  const [zoom, setZoom] = useState(13);

  // Carregar dados ao montar o componente
  useEffect(() => {
    const fetchData = async () => {
      await carregarLocalidades();
      await carregarAgentes();
      
      // Se for edição, carregar dados da propriedade
      if (id) {
        await carregarPropriedades();
        const propriedade = propriedades.find(p => p.id === parseInt(id));
        if (propriedade) {
          setFormData({
            id: propriedade.id,
            nome: propriedade.nome,
            nome_proprietario: propriedade.nome_proprietario,
            cpf_cnpj: propriedade.cpf_cnpj || '',
            telefone: propriedade.telefone,
            status: propriedade.status,
            agente_id: propriedade.agente_id || null,
            localidade_id: propriedade.localidade_id || null,
            latitude: propriedade.latitude,
            longitude: propriedade.longitude
          });
          
          // Atualizar posição no mapa
          if (propriedade.latitude && propriedade.longitude) {
            const pos = [propriedade.latitude, propriedade.longitude];
            setPosition(pos);
            setMapCenter(pos);
            setZoom(15);
          }
        }
      }
    };
    
    fetchData();
  }, [id, carregarPropriedades, carregarLocalidades, carregarAgentes]);

  // Atualizar estado do formulário
  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;
    
    // Converter campos que devem ser null quando vazios
    if ((name === 'localidade_id' || name === 'agente_id') && value === '') {
      processedValue = null;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
    
    // Limpar erro do campo
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Atualizar posição no mapa
  useEffect(() => {
    if (position) {
      setFormData(prev => ({
        ...prev,
        latitude: position[0],
        longitude: position[1]
      }));
    }
  }, [position]);

  // Validar formulário
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome da propriedade é obrigatório';
    }
    
    if (!formData.nome_proprietario.trim()) {
      newErrors.nome_proprietario = 'Nome do proprietário é obrigatório';
    }
    
    if (!formData.telefone.trim()) {
      newErrors.telefone = 'Telefone é obrigatório';
    }
    
    if (!formData.localidade_id) {
      newErrors.localidade_id = 'Localidade é obrigatória';
    }
    
    if (!formData.latitude || !formData.longitude) {
      newErrors.location = 'Selecione a localização no mapa';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Enviar formulário
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      const success = await salvarPropriedade(formData);
      
      if (success) {
        navigate('/fossas-septicas/propriedades');
      }
    }
  };

  return (
    <div className="card">
      <div style={{ marginBottom: '20px' }}>
        <Link to="/fossas-septicas/propriedades" style={{ 
          display: 'inline-flex', 
          alignItems: 'center', 
          color: '#2e7d32', 
          textDecoration: 'none',
          fontSize: '14px',
          gap: '5px'
        }}>
          <ArrowLeft size={16} />
          Voltar para Propriedades
        </Link>
        <h2 style={{ marginTop: '10px' }}>{id ? 'Editar' : 'Cadastrar'} Propriedade Rural</h2>
      </div>
      
      <form onSubmit={handleSubmit}>
        <div style={formContainerStyle}>
          <h3 style={{ marginTop: 0, marginBottom: '15px', fontSize: '18px' }}>Informações Básicas</h3>
          
          <div style={getFormGridStyle(2)}>
            <div style={formGroupStyle}>
              <label style={labelStyle}>
                Nome da Propriedade
              </label>
              <input
                type="text"
                name="nome"
                value={formData.nome}
                onChange={handleChange}
                style={getInputStyle({ error: !!errors.nome })}
                placeholder="Ex: Sítio São João"
              />
              {errors.nome && <div style={errorMessageStyle}>{errors.nome}</div>}
            </div>
            
            <div style={formGroupStyle}>
              <label style={labelStyle}>
                Nome do Proprietário
              </label>
              <input
                type="text"
                name="nome_proprietario"
                value={formData.nome_proprietario}
                onChange={handleChange}
                style={getInputStyle({ error: !!errors.nome_proprietario })}
                placeholder="Ex: João da Silva"
              />
              {errors.nome_proprietario && <div style={errorMessageStyle}>{errors.nome_proprietario}</div>}
            </div>
            
            <div style={formGroupStyle}>
              <label style={labelStyle}>
                CPF/CNPJ (opcional)
              </label>
              <input
                type="text"
                name="cpf_cnpj"
                value={formData.cpf_cnpj}
                onChange={handleChange}
                style={getInputStyle()}
                placeholder="Ex: 123.456.789-00"
              />
            </div>
            
            <div style={formGroupStyle}>
              <label style={labelStyle}>
                Telefone
              </label>
              <input
                type="text"
                name="telefone"
                value={formData.telefone}
                onChange={handleChange}
                style={getInputStyle({ error: !!errors.telefone })}
                placeholder="Ex: (27) 99999-9999"
              />
              {errors.telefone && <div style={errorMessageStyle}>{errors.telefone}</div>}
            </div>
            
            <div style={formGroupStyle}>
              <label style={labelStyle}>
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                style={getSelectStyle()}
              >
                <option value="Não Mapeada">Não Mapeada</option>
                <option value="Mapeada">Mapeada</option>
                <option value="Em Análise">Em Análise</option>
              </select>
            </div>
            
            <div style={formGroupStyle}>
              <label style={labelStyle}>
                Agente Responsável (opcional)
              </label>
              <select
                name="agente_id"
                value={formData.agente_id || ''}
                onChange={handleChange}
                style={getSelectStyle()}
              >
                <option value="">Selecione um agente</option>
                {agentes.filter(a => a.ativo).map(agente => (
                  <option key={agente.id} value={agente.id}>{agente.nome}</option>
                ))}
              </select>
            </div>
            
            <div style={formGroupStyle}>
              <label style={labelStyle}>
                Localidade
              </label>
              <select
                name="localidade_id"
                value={formData.localidade_id || ''}
                onChange={handleChange}
                style={getSelectStyle({ error: !!errors.localidade_id })}
              >
                <option value="">Selecione uma localidade</option>
                {localidades.map(loc => (
                  <option key={loc.id} value={loc.id}>{loc.descricao}</option>
                ))}
              </select>
              {errors.localidade_id && <div style={errorMessageStyle}>{errors.localidade_id}</div>}
            </div>
          </div>
        </div>
        
        <div style={formContainerStyle}>
          <h3 style={{ 
            marginTop: 0, 
            marginBottom: '15px', 
            fontSize: '18px',
            display: 'flex',
            alignItems: 'center'
          }}>
            <MapPin size={18} style={{ marginRight: '8px', color: '#2e7d32' }} />
            Localização da Propriedade
          </h3>
          
          <p style={{ marginBottom: '15px', color: '#666' }}>
            Clique no mapa para selecionar a localização exata da propriedade.
          </p>
          
          {errors.location && <div style={{ 
            color: '#dc3545', 
            fontSize: '14px', 
            marginBottom: '10px',
            padding: '8px 12px',
            backgroundColor: '#f8d7da',
            borderRadius: '4px'
          }}>{errors.location}</div>}
          
          <div style={{ 
            height: '400px',
            borderRadius: '8px',
            overflow: 'hidden',
            marginBottom: '15px',
            border: '1px solid #ced4da'
          }}>
            <MapContainer 
              center={mapCenter} 
              zoom={zoom} 
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              <LocationSelector position={position} setPosition={setPosition} />
            </MapContainer>
          </div>
          
          {position && (
            <div style={{ 
              padding: '10px 15px',
              backgroundColor: '#f8f9fa',
              borderRadius: '4px',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center'
            }}>
              <MapPin size={16} style={{ marginRight: '8px', color: '#2e7d32' }} />
              <strong>Coordenadas:</strong>&nbsp;{position[0].toFixed(6)}, {position[1].toFixed(6)}
            </div>
          )}
        </div>
        
        <div style={formActionsStyle}>
          <button 
            type="button" 
            onClick={() => navigate('/fossas-septicas/propriedades')}
            style={secondaryButtonStyle}
          >
            Cancelar
          </button>
          <button 
            type="submit" 
            disabled={loading.propriedades}
            style={applyDisabledStyle(primaryButtonStyle, loading.propriedades)}
          >
            {loading.propriedades ? 'Salvando...' : id ? 'Atualizar' : 'Cadastrar'}
          </button>
        </div>
      </form>
      
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default CadastroPropriedadeRural;